import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useNeeds } from "@/hooks/useStaffing";
import NeedCreateModal from "@/components/admin/recruitment/NeedCreateModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";

const STATUS_COLORS = {
  open: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  in_progress: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  cancelled: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  filled: "bg-purple-500/10 text-purple-300 border-purple-500/20",
};

const STATUS_LABELS = {
  open: "Ouvert",
  in_progress: "En cours",
  cancelled: "Annulé",
  filled: "Pourvu",
};

export default function NeedsListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { needs, loading, error, createNeed, deleteNeed } = useNeeds();
  const [showCreateModal, setShowCreateModal] = useState(
    searchParams.get("create") === "true" || searchParams.get("new") === "true"
  );
  const [needToDelete, setNeedToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFeedback, setActionFeedback] = useState({ type: null, message: null });

  const filteredNeeds = useMemo(() => {
    return needs.filter((item) => {
      if (
        item.is_deleted === true ||
        String(item.status || "").trim().toLowerCase() === "deleted" ||
        (item.deleted_at != null && String(item.deleted_at).trim() !== "")
      ) {
        return false;
      }
      if (statusFilter !== "all" && item.status !== statusFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (item.title || "").toLowerCase().includes(q);
        const clientMatch = (item.client_id || "").toLowerCase().includes(q);
        const skillsMatch = (item.required_skills || []).some((s) =>
          String(s).toLowerCase().includes(q)
        );
        return titleMatch || clientMatch || skillsMatch;
      }
      return true;
    });
  }, [needs, statusFilter, searchQuery]);

  return (
    <div className="space-y-6 pb-12" data-testid="needs-list-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
            Besoins Clients
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Gérez les missions et lancez l&apos;évaluation IA des candidats en temps réel.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-blue-500 transition-all self-start sm:self-auto"
          data-testid="create-need-button"
        >
          <Plus className="h-4 w-4" />
          + Nouveau besoin client
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par intitulé, client, technologie..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="needs-search-input"
          />
        </div>

        {/* Status Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          {[
            { id: "all", label: "Tous" },
            { id: "open", label: "Ouverts" },
            { id: "in_progress", label: "En cours" },
            { id: "filled", label: "Pourvus" },
            { id: "cancelled", label: "Annulés" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === tab.id
                  ? "bg-blue-600 text-white font-semibold"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-rose-300 text-xs">
          {error}
        </div>
      )}

      {/* Action feedback banners */}
      {actionFeedback.message && (
        <div
          className={`rounded-xl p-4 text-xs flex items-center justify-between border ${
            actionFeedback.type === "success"
              ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
              : "bg-rose-500/10 border-rose-500/20 text-rose-300"
          }`}
          role={actionFeedback.type === "success" ? "status" : "alert"}
          data-testid="needs-action-feedback"
        >
          <div className="flex items-center gap-2">
            {actionFeedback.type === "success" ? (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0" />
            )}
            <span>{actionFeedback.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionFeedback({ type: null, message: null })}
            className="p-1 hover:text-white transition-colors"
            aria-label="Fermer le message"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredNeeds.length === 0 && (
        <div className="text-center py-16 glass rounded-2xl border border-white/10 text-slate-500">
          <Briefcase className="h-10 w-10 mx-auto text-slate-600 mb-3" />
          <p className="text-base font-semibold text-slate-300">Aucun besoin correspondant</p>
          <p className="text-xs text-slate-500 mt-1">
            {searchQuery
              ? "Essayez d'ajuster votre recherche."
              : "Créez un besoin en collant son descriptif ou en complétant les informations."}
          </p>
        </div>
      )}

      {/* Needs list */}
      {!loading && filteredNeeds.length > 0 && (
        <div className="grid gap-3.5">
          {filteredNeeds.map((need) => (
            <div
              key={need._id}
              className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
              data-testid={`need-card-${need._id}`}
            >
              <div className="space-y-2 flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="font-semibold text-white text-base">
                    {need.title}
                  </h3>
                  <span
                    className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
                      STATUS_COLORS[need.status] || STATUS_COLORS.open
                    }`}
                  >
                    {STATUS_LABELS[need.status] || need.status}
                  </span>
                  {need.client_id && (
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-blue-300 border border-white/10">
                      Client : {need.client_id}
                    </span>
                  )}
                  {need.input_mode === "quick" && (
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      Descriptif analysé
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                  {(need.contract_types?.length || need.contract_type) ? (
                    <span className="flex items-center gap-1">
                      <span className="text-slate-500">Contrat :</span> {(need.contract_types || [need.contract_type]).join(" · ")}
                    </span>
                  ) : <span>Ouvert à tous les types de contrats</span>}
                  {need.start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-slate-500" />
                      Démarrage : {need.start_date}
                    </span>
                  )}
                  {need.created_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-slate-500" />
                      Créé le {new Date(need.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  )}
                </div>

                {/* Skills tags */}
                {need.required_skills && need.required_skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {need.required_skills.slice(0, 6).map((skill, idx) => (
                      <span
                        key={idx}
                        className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700/60"
                      >
                        {skill}
                      </span>
                    ))}
                    {need.required_skills.length > 6 && (
                      <span className="text-[11px] px-1.5 py-0.5 text-slate-500">
                        +{need.required_skills.length - 6}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/admin/recrutement/besoins/${need._id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-xl text-xs font-semibold transition-colors"
                  data-testid={`view-need-link-${need._id}`}
                >
                  Matching & Candidats
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={() => setNeedToDelete(need)}
                  className="p-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-colors"
                  title="Supprimer le besoin"
                  aria-label={`Supprimer le besoin ${need.title || ""}`}
                  data-testid={`delete-need-${need._id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Creation Modal with direct navigation to matching */}
      <NeedCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateNeed={createNeed}
        onCreated={(created) => {
          const id = created?._id || created?.id;
          if (id) {
            navigate(`/admin/recrutement/besoins/${id}`);
          }
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(needToDelete)}
        onClose={() => setNeedToDelete(null)}
        title="Supprimer le besoin client"
        message={`Voulez-vous vraiment supprimer définitivement le besoin « ${needToDelete?.title || ""} » ? Cette action retirera la mission de la liste. Cette opération est irréversible.`}
        confirmText="Confirmer la suppression"
        cancelText="Annuler"
        variant="danger"
        isSubmitting={deleting}
        onConfirm={async () => {
          if (!needToDelete) return;
          setDeleting(true);
          setActionFeedback({ type: null, message: null });
          try {
            const title = needToDelete.title || "Besoin";
            await deleteNeed(needToDelete._id);
            setNeedToDelete(null);
            setActionFeedback({
              type: "success",
              message: `Le besoin « ${title} » a été supprimé avec succès.`,
            });
          } catch (err) {
            const detail =
              err?.response?.data?.detail ||
              err?.message ||
              "Échec de la suppression du besoin client.";
            setActionFeedback({
              type: "error",
              message: detail,
            });
            setNeedToDelete(null);
          } finally {
            setDeleting(false);
          }
        }}
        testId="delete-need-modal"
      />
    </div>
  );
}
